import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Flex,
  Badge,
  Card,
  CardBody,
  HStack,
  VStack,
  Icon,
  Tooltip
} from '@chakra-ui/react';
import { CheckCircleIcon, WarningIcon } from '@chakra-ui/icons';
import { AppContext } from './AppContext';
import HeaderMenus from './HeaderMenus';

const ClaimAnalysisList = () => {
  const { accessToken } = useContext(AppContext);
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReports = async () => {
      if (!accessToken) return;
      setIsLoading(true);
      try {
        const response = await fetch(`${window.env.REACT_APP_REVEDY_API_HOST}/api/coding-variance/all`, {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const data = await response.json();
        if (data.status === 'success') {
          // Filter to only show reports that have payer_denial_evaluation (NMA pipeline output)
          // or cpt_variance.summary (Standard pipeline output)
          const validReports = data.reports.filter(r => 
            r.payer_denial_evaluation || (r.cpt_variance && r.cpt_variance.summary)
          );
          setReports(validReports);
        } else {
          throw new Error(data.message || 'Failed to fetch reports');
        }
      } catch (err) {
        console.error("Error fetching variance reports:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [accessToken]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getPathwayColor = (pathway) => {
    if (!pathway) return 'gray';
    const p = pathway.toLowerCase();
    if (p.includes('correction')) return 'orange';
    if (p.includes('appeal')) return 'red';
    if (p.includes('arbitration')) return 'purple';
    return 'blue';
  };

  // Calculate Summary Metrics
  const summary = useMemo(() => {
    let totalBilled = 0;
    let totalPaid = 0;
    
    reports.forEach(report => {
      const evalData = report.payer_denial_evaluation || (report.cpt_variance && report.cpt_variance.summary) || {};
      totalBilled += (evalData.billed_total || 0);
      totalPaid += (evalData.paid_total || 0);
    });

    return {
      totalCases: reports.length,
      totalBilled,
      totalPaid,
      totalVariance: totalBilled - totalPaid
    };
  }, [reports]);

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto">
        <Flex mb={6} align="center" justify="space-between">
          <VStack align="start" spacing={1}>
            <Heading size="lg">Revenue Cycle: Claim Variance Dashboard</Heading>
            <Text color="gray.500">Aggregate view of analyzed claims, financial variance, and AI strategy generation.</Text>
          </VStack>
        </Flex>

        {isLoading ? (
          <Flex justify="center" p={12}>
            <Spinner size="xl" />
          </Flex>
        ) : error ? (
          <Card bg="red.50" borderColor="red.200" borderWidth="1px">
            <CardBody>
              <Text color="red.800">Error loading dashboard: {error}</Text>
            </CardBody>
          </Card>
        ) : reports.length === 0 ? (
          <Card>
            <CardBody>
              <Text textAlign="center" color="gray.500" py={8}>
                No analyzed claims found. Run the Claim Analysis pipeline on a case to see it here.
              </Text>
            </CardBody>
          </Card>
        ) : (
          <VStack spacing={6} align="stretch">
            
            {/* Summary Cards */}
            <HStack spacing={6} align="stretch">
              <Card flex={1} bg="gray.50">
                <CardBody>
                  <VStack align="start" spacing={1}>
                    <Text color="gray.500" fontSize="xs" fontWeight="bold" textTransform="uppercase">Total Cases Analyzed</Text>
                    <Heading size="lg">{summary.totalCases}</Heading>
                  </VStack>
                </CardBody>
              </Card>
              <Card flex={1}>
                <CardBody>
                  <VStack align="start" spacing={1}>
                    <Text color="gray.500" fontSize="xs" fontWeight="bold" textTransform="uppercase">Total Billed</Text>
                    <Heading size="lg">{formatCurrency(summary.totalBilled)}</Heading>
                  </VStack>
                </CardBody>
              </Card>
              <Card flex={1}>
                <CardBody>
                  <VStack align="start" spacing={1}>
                    <Text color="gray.500" fontSize="xs" fontWeight="bold" textTransform="uppercase">Total Paid</Text>
                    <Heading size="lg">{formatCurrency(summary.totalPaid)}</Heading>
                  </VStack>
                </CardBody>
              </Card>
              <Card flex={1} bg={summary.totalVariance > 0 ? "red.50" : "green.50"} borderColor={summary.totalVariance > 0 ? "red.200" : "green.200"} borderWidth="1px">
                <CardBody>
                  <VStack align="start" spacing={1}>
                    <Text color={summary.totalVariance > 0 ? "red.800" : "green.800"} fontSize="xs" fontWeight="bold" textTransform="uppercase">Total Variance (Loss)</Text>
                    <Heading size="lg" color={summary.totalVariance > 0 ? "red.600" : "green.600"}>
                      {formatCurrency(summary.totalVariance)}
                    </Heading>
                  </VStack>
                </CardBody>
              </Card>
            </HStack>

            {/* Dense Data Table */}
            <Card>
              <Box overflowX="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50">
                    <Tr>
                      <Th>Case ID</Th>
                      <Th>Payer</Th>
                      <Th isNumeric>Billed</Th>
                      <Th isNumeric>Paid</Th>
                      <Th isNumeric>Variance</Th>
                      <Th>Recommended Action</Th>
                      <Th textAlign="center">Strategy Ready</Th>
                      <Th>Date Analyzed</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {reports.map((report) => {
                      const evalData = report.payer_denial_evaluation || (report.cpt_variance && report.cpt_variance.summary) || {};
                      const billed = evalData.billed_total || 0;
                      const paid = evalData.paid_total || 0;
                      const variance = billed - paid;
                      const pathway = evalData.primary_pathway || 'UNKNOWN';
                      
                      // Check if strategy was generated
                      const hasStrategy = report.eob_data && report.eob_data.draft_content && 
                        (report.eob_data.draft_content.appeal_letter || report.eob_data.draft_content.corrected_claim_memo);

                      return (
                        <Tr key={report.id} _hover={{ bg: "gray.50" }}>
                          <Td fontWeight="bold">
                            <RouterLink to={`/claim-analysis/${report.external_case_id}`} style={{ color: '#3182ce', textDecoration: 'underline' }}>
                              {report.external_case_id}
                            </RouterLink>
                          </Td>
                          <Td>
                            <Text fontSize="sm" fontWeight="medium" noOfLines={1} maxWidth="200px">
                              {report.carrier_name !== 'Unknown' ? report.carrier_name : report.payer_type}
                            </Text>
                          </Td>
                          <Td isNumeric>{formatCurrency(billed)}</Td>
                          <Td isNumeric>{formatCurrency(paid)}</Td>
                          <Td isNumeric color={variance > 0 ? "red.500" : "inherit"} fontWeight={variance > 0 ? "bold" : "normal"}>
                            {formatCurrency(variance)}
                          </Td>
                          <Td>
                            <Badge colorScheme={getPathwayColor(pathway)} variant="subtle">
                              {pathway.replace('_', ' ').toUpperCase()}
                            </Badge>
                          </Td>
                          <Td textAlign="center">
                            {hasStrategy ? (
                              <Tooltip label="AI Strategy Generated">
                                <Icon as={CheckCircleIcon} color="green.500" />
                              </Tooltip>
                            ) : (
                              <Tooltip label="No Strategy Generated">
                                <Icon as={WarningIcon} color="gray.300" />
                              </Tooltip>
                            )}
                          </Td>
                          <Td>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(report.created_at).toLocaleDateString()}
                            </Text>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            </Card>

          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default ClaimAnalysisList;